import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ExpandedState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  GripVertical,
  Pencil,
  Plus,
  Printer,
  SlidersHorizontal,
  Trash2,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { Button } from "../ui/Button";
import { cn } from "../ui/cn";
import { useConfirm } from "../ui/ConfirmDialog";
import { useIsCompact } from "../ui/useMediaQuery";
import { CardList } from "./CardList";
import { EditorCell } from "./EditorCell";
import { MultiSelect } from "../ui/MultiSelect";
import type { DateRangeFilterValue, FilterKind, RangeFilterValue } from "./columns";
import type { RowEditorConfig } from "./rowEditor";
import type { ServerTableMode, TableQuery } from "./serverTable";
import { readColumnOrder, readTableState, writeColumnOrder, writeTableState, type TableStateSnapshot } from "./tableState";
import { printTable } from "./printTable";
import { TableToolbar, type TableAction } from "./TableToolbar";
import { useRowEditor } from "./useRowEditor";

/**
 * Filter functions for the two kinds TanStack has no built-in for.
 *
 * `multiselect` matches a cell against a chosen set; `range` against a
 * from/to pair where either side may be open. Both tolerate an empty
 * selection by passing every row, so a half-filled filter never blanks the
 * grid.
 */
const FILTER_FNS = {
  /**
   * Exact match, which is what picking one entry from a list means.
   *
   * Without this a select falls through to TanStack's `auto`, and `auto` on a
   * string column is a case-insensitive *contains*. Choosing one client then
   * also matched every other client whose name contained it. Invisible on the
   * short enum columns selects were first used for, wrong the moment the
   * options are names.
   *
   * Compared as strings because the filter row's option values always are,
   * while the accessor behind them may not be.
   */
  select: (row: { getValue: (id: string) => unknown }, columnId: string, filterValue: unknown) => {
    if (filterValue === undefined || filterValue === null || filterValue === "") return true;
    return String(row.getValue(columnId) ?? "") === String(filterValue);
  },
  multiselect: (row: { getValue: (id: string) => unknown }, columnId: string, filterValue: unknown) => {
    const chosen = filterValue as string[] | undefined;
    if (!chosen || chosen.length === 0) return true;
    const value = row.getValue(columnId);
    if (Array.isArray(value)) return value.some((entry) => chosen.includes(String(entry)));
    return chosen.includes(String(value ?? ""));
  },
  range: (row: { getValue: (id: string) => unknown }, columnId: string, filterValue: unknown) => {
    const [from, to] = (filterValue as RangeFilterValue | undefined) ?? [undefined, undefined];
    if (from === undefined && to === undefined) return true;
    const raw = row.getValue(columnId);
    const value = typeof raw === "number" ? raw : Number(raw);
    if (Number.isNaN(value)) return false;
    if (from !== undefined && value < from) return false;
    if (to !== undefined && value > to) return false;
    return true;
  },
  /**
   * Same from/to shape as `range`, compared as ISO `YYYY-MM-DD` strings —
   * lexicographic order is chronological order for that format, so no Date
   * parsing is needed.
   *
   * Only the first 10 characters of the cell value are compared, so a column
   * whose accessor renders a full timestamp (`2026-09-08T13:45:00`, or
   * `2026-09-08 13:45:00`) still filters correctly at day granularity — a
   * `to` bound of today would otherwise exclude every row timestamped later
   * than midnight, since `"...T13:45:00" > "2026-09-08"` lexicographically.
   * Any ISO-prefixed value works without the accessor needing to change.
   */
  daterange: (row: { getValue: (id: string) => unknown }, columnId: string, filterValue: unknown) => {
    const [from, to] = (filterValue as DateRangeFilterValue | undefined) ?? [undefined, undefined];
    if (from === undefined && to === undefined) return true;
    const value = String(row.getValue(columnId) ?? "").slice(0, 10);
    if (value === "") return false;
    if (from !== undefined && value < from) return false;
    if (to !== undefined && value > to) return false;
    return true;
  },
} as const;

/** Above this many rows the body virtualizes. */
const VIRTUALIZE_ABOVE = 60;
/** A held column width: never more than this, whatever its longest value. */
const MAX_FROZEN_WIDTH = 360;
/** A body cell's horizontal padding (px-3 on each side). */
const CELL_PADDING = 24;

/** Measures text in the body's font, through one canvas reused by every call. */
function textMeasurer(sample: HTMLElement): (text: string) => number {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return () => 0;
  const style = getComputedStyle(sample);
  context.font = `13px ${style.fontFamily}`;
  return (text) => context.measureText(text).width;
}
const ROW_HEIGHT = 38;

/**
 * How close to the end of the loaded rows the scroll gets before the next page
 * is asked for, in rows. Wide enough that the next page normally arrives before
 * the user reaches the gap, narrow enough not to fetch pages nobody looks at.
 */
const LOAD_AHEAD_ROWS = 10;

export interface DataTableHandle {
  /** The built-in bulk delete: confirmation, `onDeleteSelected`, selection reset. */
  deleteSelected: () => Promise<void>;
  clearSelection: () => void;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  /** Stable row identity — required for selection to survive refetches. */
  getRowId: (row: T) => string;
  loading?: boolean;
  selectable?: boolean;
  /**
   * The selected rows. Only ever the ones currently loaded — on a server-paged
   * grid a selection can name rows that are not, and those have no object to
   * hand over. Use `onSelectionIdsChange` for anything that acts on the whole
   * selection; use this for what needs a row's contents, like a single-row
   * dialog.
   */
  onSelectionChange?: (rows: T[]) => void;
  /**
   * The ids of every selected row, loaded or not.
   *
   * This is the selection in full, and the one bulk actions want: they send ids
   * to a batch endpoint anyway. On a client-side grid it always agrees with
   * `onSelectionChange`; on a server-paged one it is the half that survives
   * "select all 12,480 matching".
   */
  onSelectionIdsChange?: (ids: string[]) => void;
  onRowClick?: (row: T) => void;
  /** Screen-specific actions, shown before the built-in grid utilities. */
  actions?: TableAction[];
  /** Counts and totals rendered next to the row badges. */
  extraBadges?: ReactNode;
  /**
   * The heading of the printed list (the toolbar's Print button prints every
   * row the filters keep, not just the screenful). Defaults to the page's title.
   */
  printTitle?: string;
  /** Leaves the Print button out, for a grid where printing makes no sense. */
  hidePrint?: boolean;
  /** Arbitrary toolbar content — menus and anything a plain action can't express. */
  toolbarExtra?: ReactNode;
  /** Rendered right after the search, before the actions — for a switch that changes how the grid reads. */
  toolbarLead?: ReactNode;
  emptyMessage?: string;
  /**
   * Turns on inline row editing — add, edit and delete in place, the way the
   * `EditorGrid` subclasses worked. Omit for a read-only grid.
   */
  editor?: RowEditorConfig<T>;
  /**
   * Deletes the current selection. Renders a write-gated Delete button in the
   * toolbar, behind a confirmation, and clears the selection afterwards.
   */
  onDeleteSelected?: (rows: T[]) => Promise<void> | void;
  /**
   * The same built-in delete, by id rather than by row — what a server-paged
   * grid needs, where a selection can name rows that were never loaded.
   * Preferred over `onDeleteSelected` when both are given.
   */
  onDeleteSelectedIds?: (ids: string[]) => Promise<void> | void;
  /** Renders the built-in Delete action as a square icon button. */
  deleteIconOnly?: boolean;
  /**
   * Leaves the built-in Delete button out of the toolbar. For a page that
   * offers the delete from a menu of its own and fires it through `handle` —
   * the flow (confirmation, selection reset) stays the grid's either way.
   */
  hideDeleteButton?: boolean;
  /**
   * What the page can ask the grid to do. Filled on mount, cleared on unmount.
   * The grid owns the selection and the delete confirmation, so a page that
   * puts "Delete selected" in its own menu reaches them through here instead
   * of rebuilding them.
   */
  handle?: MutableRefObject<DataTableHandle | null>;
  /**
   * Confirmation copy for the bulk delete. Return `requireText` to make the
   * user type a phrase first — worth it for wide, irreversible deletes.
   */
  deleteSelectedConfirm?: (count: number) => {
    title: string;
    message?: string;
    requireText?: string;
    consequences?: string[];
  };
  /** Sort applied on first render, mirroring each Vaadin grid's default order. */
  initialSorting?: SortingState;
  /** Column filters applied on first render (the decor screen opens on ACTIVE). */
  initialFilters?: ColumnFiltersState;
  /**
   * Trailing per-row controls. Unlike `editor.rowActions` these render on a
   * read-only grid too, which is how the Vaadin `GridFeature.VIEW`/`DELETE`
   * row buttons worked.
   */
  rowActions?: (row: T) => ReactNode;
  /** Header of the row-action column. */
  rowActionsHeader?: string;
  /**
   * Extra classes for a row, from its data. For state the grid does not own —
   * a lot ticked off on the planner's worksheet — where the whole line should
   * read differently, not one cell of it.
   */
  rowClassName?: (row: T) => string | undefined;
  /** Turns the grid into a tree: return a row's children, or undefined for a leaf. */
  getSubRows?: (row: T) => T[] | undefined;
  /** Expand every parent on load — what the production-job tree does. */
  defaultExpanded?: boolean;
  /**
   * The tree's parents are groupings, not records — a product family over its
   * products. Filters and the search then match from the leaves up: a family
   * stays when one of its products matches, showing only the products that
   * do. A selection reports leaves — ticking a family ticks its products — and
   * the row counts count leaves, since that is what the grid is a list of.
   */
  groupingParents?: boolean;
  /**
   * Drag-to-reorder. Receives the **visible** rows in their new order, so a
   * grid filtered to one product reorders only that product's rows — which is
   * exactly how the decor grid enabled dragging once a product was picked.
   * Sorting switches it off: dragging against a sort would write an order the
   * next render immediately contradicts.
   */
  onReorder?: (rows: T[]) => void;
  /**
   * Row height in pixels. Raise it on grids whose cells carry something taller
   * than text — a thumbnail column, say — because the virtualizer measures
   * rows from this number and a cell that outgrows it drifts out of place.
   */
  rowHeight?: number;
  /**
   * Remembers this grid's filters, sort, selection, column choices and scroll
   * position while the user is away from it, keyed by the route subtree it
   * belongs to (`/products`).
   *
   * State survives anywhere under that path, so opening a row and coming back
   * lands on the grid as it was left, and is dropped as soon as the user picks
   * a different section from the menu. Omit it and the grid starts fresh every
   * time, which is the right answer for a grid inside a detail tab.
   */
  stateKey?: string;
  /**
   * Turns the grid over to a server-paged result set: it shows the rows it has
   * been given, asks for the next page as the scroll nears the end, and hands
   * sorting, filtering and search to the database rather than performing them
   * on the rows it happens to hold. Build one with `useServerTable`.
   *
   * Three things change, and a screen has to mean all three or the grid tells
   * a different story from the data:
   *
   * 1. **A column's id is a field name.** It goes to the server verbatim in
   *    `sort` and `filter.<name>`, and an id the repository does not publish
   *    is a 400 — deliberately, so a renamed column fails at once instead of
   *    silently showing unfiltered rows. A column with nothing behind it
   *    (a thumbnail, a computed badge) must declare `filter: "none"` and
   *    `enableSorting: false`.
   * 2. **`select` and `multiselect` columns must declare `filterOptions`.**
   *    The options are otherwise collected from the loaded rows, which is no
   *    longer a complete sample of the values — a dropdown built from one page
   *    would quietly hide most of the choices.
   * 3. **The badges come from the server**, since the browser can no longer
   *    count what it cannot see.
   *
   * Changing a filter or the search clears the selection, which a client-side
   * grid does not do. There it was harmless — a row hidden by a filter came
   * back the moment the filter cleared, and the user could always review what
   * was ticked. Here a selection can outlive the only view that ever showed
   * it, and the bulk actions would still act on every row in it. Re-sorting
   * keeps the selection: the same rows match, in a different order.
   *
   * Omit it — as all but a handful of screens do — and nothing here changes.
   */
  server?: ServerTableMode;
}

/**
 * The suite's grid — the replacement for `ManagedGrid`.
 *
 * Carries the whole feature set every screen expects: multi-select, multi-sort
 * (shift-click), a per-column filter row, global search, column visibility with
 * default/all presets, live row-count badges, virtualization for large sets,
 * and a card layout below the `md` breakpoint. Screens supply data and column
 * definitions; nothing here is per-screen.
 *
 * Filtering and sorting run client-side over the rows the screen supplies,
 * which is right for the grids that can hold all of theirs. Pass `server` and
 * the same UI is driven from a paged endpoint instead — same columns, same
 * toolbar, same filter row, with the work done in the database.
 */
export function DataTable<T>({
  data,
  columns,
  getRowId,
  loading = false,
  selectable = false,
  onSelectionChange,
  onRowClick,
  actions = [],
  extraBadges,
  toolbarExtra,
  toolbarLead,
  emptyMessage,
  editor,
  onDeleteSelected,
  onDeleteSelectedIds,
  deleteIconOnly,
  hideDeleteButton = false,
  printTitle,
  hidePrint = false,
  handle,
  deleteSelectedConfirm,
  initialSorting,
  initialFilters,
  rowActions,
  rowActionsHeader,
  rowClassName,
  getSubRows,
  defaultExpanded = false,
  groupingParents = false,
  onReorder,
  rowHeight = ROW_HEIGHT,
  stateKey,
  server,
  onSelectionIdsChange,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const compact = useIsCompact();
  const rowEditor = useRowEditor(editor, getRowId);
  const editable = Boolean(editor) && canWrite;

  // A repeated row id breaks the table silently: TanStack keys its row models
  // and selection by id, and React keys the rendered rows by it, so two rows
  // sharing one id render as one element that keeps stale content once a
  // filter or sort changes what is visible. The row count is right and the
  // cells are not, which reads as "the filter is broken". Flag it at the
  // source, once per table instance.
  const warnedDuplicateIds = useRef(false);
  useEffect(() => {
    if (warnedDuplicateIds.current) return;
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const row of data) {
      const id = getRowId(row);
      if (seen.has(id)) duplicates.add(id);
      seen.add(id);
    }
    if (duplicates.size === 0) return;
    warnedDuplicateIds.current = true;
    console.warn(
      `DataTable: ${duplicates.size} row id(s) repeat; getRowId must be unique per row or filtering and selection misbehave. First few:`,
      [...duplicates].slice(0, 5),
    );
  }, [data, getRowId]);

  // Read once, at mount. A grid with no stateKey, or one whose section the
  // user has left, gets undefined here and falls back to its initial props.
  const [restored] = useState<TableStateSnapshot | undefined>(() =>
    stateKey ? readTableState(stateKey) : undefined,
  );

  const [sorting, setSorting] = useState<SortingState>(restored?.sorting ?? initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    restored?.columnFilters ?? initialFilters ?? [],
  );
  const [expanded, setExpanded] = useState<ExpandedState>(
    restored?.expanded ?? (defaultExpanded ? true : {}),
  );
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // Column order: empty means the definition order. Read from localStorage,
  // not the session snapshot — an arrangement is a preference, see tableState.
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    () => (stateKey ? readColumnOrder(stateKey) : undefined) ?? [],
  );
  const [columnDrag, setColumnDrag] = useState<{ id: string; over: string | null; after: boolean } | null>(null);
  const [globalFilter, setGlobalFilter] = useState(restored?.globalFilter ?? "");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(restored?.rowSelection ?? {});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () =>
      restored?.columnVisibility ??
      Object.fromEntries(
        columns
          .filter((column) => column.meta?.defaultVisible === false)
          .map((column) => [String(column.id), false]),
      ),
  );

  // A column declares its filter kind in meta; the matching filterFn is wired
  // here so screens never have to name it.
  useEffect(() => {
    if (stateKey) writeColumnOrder(stateKey, columnOrder);
  }, [stateKey, columnOrder]);

  /**
   * Moves the dragged column beside the one it was dropped on.
   *
   * Works on the visible leaf order, which is the order the user sees and
   * drags within; hidden columns keep their place relative to their
   * neighbours. The fixed columns — selection, tree toggle, row menu — are
   * not in the ordering at all and cannot be moved or landed on.
   */
  const dropColumn = (draggedId: string, targetId: string, after: boolean) => {
    if (draggedId === targetId) return;
    const current = table.getAllLeafColumns().map((column) => column.id);
    const without = current.filter((id) => id !== draggedId);
    const at = without.indexOf(targetId);
    if (at === -1) return;
    without.splice(after ? at + 1 : at, 0, draggedId);
    setColumnOrder(without);
  };

  const resolvedColumns = useMemo(
    () =>
      columns.map((column) => {
        if (column.filterFn) return column;
        const kind = column.meta?.filter;
        if (kind === "select") return { ...column, filterFn: FILTER_FNS.select as never };
        if (kind === "multiselect")
          return { ...column, filterFn: FILTER_FNS.multiselect as never };
        if (kind === "range") return { ...column, filterFn: FILTER_FNS.range as never };
        if (kind === "daterange") return { ...column, filterFn: FILTER_FNS.daterange as never };
        return column;
      }),
    [columns],
  );

  const table = useReactTable<T>({
    data,
    columns: resolvedColumns,
    getRowId,
    state: { sorting, columnFilters, globalFilter, rowSelection, columnVisibility, expanded, columnOrder },
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    enableRowSelection: selectable,
    enableMultiSort: true,
    // Server mode: the rows arrived already sorted and filtered, so doing it
    // again here would be work at best and a second, disagreeing opinion at
    // worst — one page of 100 re-sorted looks exactly like a broken sort.
    manualSorting: Boolean(server),
    manualFiltering: Boolean(server),
    ...(getSubRows ? { getSubRows } : {}),
    ...(groupingParents ? { filterFromLeafRows: true } : {}),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const rows = table.getRowModel().rows;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualize = !compact && rows.length > VIRTUALIZE_ABOVE;


  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
    enabled: virtualize,
  });

  const virtualRows = virtualize ? virtualizer.getVirtualItems() : [];
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0;

  // ── server paging ────────────────────────────────────────────────────────

  /**
   * Asks for the next page as the scroll approaches the end of the loaded
   * rows, so the list simply continues rather than stopping at a control.
   */
  const loadAhead = useCallback(() => {
    if (!server?.hasMore || server.loadingMore) return;
    const element = scrollRef.current;
    if (!element) return;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining <= rowHeight * LOAD_AHEAD_ROWS) server.loadMore();
  }, [server, rowHeight]);

  // A page that does not fill its window cannot be scrolled, so nothing would
  // ever ask for the next one: a narrow filter, a tall screen, or a short last
  // page all end up here. Runs after each page lands, and stops of its own
  // accord once the body overflows or the rows run out.
  useEffect(() => {
    if (!server?.hasMore || server.loadingMore) return;
    const element = scrollRef.current;
    if (element && element.scrollHeight <= element.clientHeight) server.loadMore();
  }, [server, rows.length]);

  /**
   * Pushes the grid's sort, filters and search out to the server whenever the
   * user changes one. The column ids travel as field names; each filter value
   * is encoded by the kind its column declares, because only the column knows
   * whether `["10", "50"]` is two choices or one range.
   */
  const pushQuery = useRef<((query: TableQuery) => void) | undefined>(undefined);
  pushQuery.current = server?.onQueryChange;
  const serverMode = Boolean(server);
  const queried = useRef(false);
  /** What last decided which rows match — the filters and the search, not the sort. */
  const matched = useRef<string | null>(null);

  // Read through a ref rather than depended on: a screen that rebuilds its
  // column array each render would otherwise re-run this effect every render,
  // and it has side effects — the scroll and the selection below.
  const columnsForQuery = useRef(resolvedColumns);
  columnsForQuery.current = resolvedColumns;

  useEffect(() => {
    if (!serverMode) return;

    const filters = serverFilters(columnFilters, columnsForQuery.current);
    pushQuery.current?.({
      sort: sorting.map((entry) => `${entry.id},${entry.desc ? "desc" : "asc"}`),
      filters,
      q: globalFilter,
    });

    const matching = JSON.stringify([filters, globalFilter]);
    const narrowed = matched.current !== null && matched.current !== matching;
    matched.current = matching;

    // Any new query invalidates the scroll position: it would drop the user
    // into the middle of rows that no longer answer the question just asked.
    if (queried.current && scrollRef.current) scrollRef.current.scrollTop = 0;

    // A changed *filter* also invalidates the selection, because it can now
    // name rows the user cannot see and has no way to review — while the bulk
    // actions would still act on every one of them. On a grid holding all its
    // rows a selection could outlive a filter honestly, since it reappeared
    // the moment the filter cleared. Here it cannot, so a new set of matching
    // rows starts a new selection. A re-sort is not a new set, and keeps it.
    if (narrowed) table.resetRowSelection();

    queried.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMode, sorting, columnFilters, globalFilter]);

  // ── remembering the grid across a trip into one of its rows ──────────────
  //
  // Written on every change rather than only on the way out, so the snapshot is
  // already correct whichever way the user leaves: a link, the back button, or
  // closing the tab.
  useEffect(() => {
    if (!stateKey) return;
    writeTableState(stateKey, {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      columnVisibility,
      expanded,
      scrollTop: scrollRef.current?.scrollTop ?? 0,
    });
  }, [stateKey, sorting, columnFilters, globalFilter, rowSelection, columnVisibility, expanded]);

  // Scroll position is the one part that changes without any state changing, so
  // it is captured on the way out instead. The element has to be read here and
  // not in the cleanup body, because by then the ref has been detached.
  useEffect(() => {
    if (!stateKey) return;
    const element = scrollRef.current;
    return () => {
      const snapshot = readTableState(stateKey);
      if (snapshot && element) {
        writeTableState(stateKey, { ...snapshot, scrollTop: element.scrollTop });
      }
    };
  }, [stateKey]);

  // Put the scroll back once there are rows to scroll through. Data usually
  // arrives a tick after mount, and scrolling an empty body just pins it at 0,
  // so this waits for the first non-empty render and then stands down.
  const scrollRestored = useRef(false);
  useLayoutEffect(() => {
    if (scrollRestored.current) return;
    const target = restored?.scrollTop ?? 0;
    if (target <= 0) {
      scrollRestored.current = true;
      return;
    }
    if (rows.length === 0 || !scrollRef.current) return;
    scrollRef.current.scrollTop = target;
    scrollRestored.current = true;
  }, [restored, rows.length]);

  // ── the selection, told to the screen around the grid ───────────────────
  //
  // One place decides what "selected" means, so it stays true however the
  // selection got there — a click, a snapshot restored on the way back into
  // the screen, or select-all naming rows that have not loaded yet. That last
  // case is why there are two channels: ids exist for every selected row,
  // objects only for the ones on hand.
  //
  // The callbacks are read through refs so a screen can pass inline arrows
  // without the notification firing on every render.

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const selectedRows = useMemo(() => {
    const pool = groupingParents
      ? table.getCoreRowModel().flatRows.filter((row) => row.subRows.length === 0)
      : table.getCoreRowModel().rows;
    return pool.filter((row) => rowSelection[row.id]).map((row) => row.original);
    // `data` is the proxy for the row model being rebuilt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection, data, groupingParents, table]);

  const notifyRows = useRef(onSelectionChange);
  notifyRows.current = onSelectionChange;
  const notifyIds = useRef(onSelectionIdsChange);
  notifyIds.current = onSelectionIdsChange;

  useEffect(() => {
    notifyRows.current?.(selectedRows);
  }, [selectedRows]);

  useEffect(() => {
    notifyIds.current?.(selectedIds);
  }, [selectedIds]);

  // ── select all ───────────────────────────────────────────────────────────

  const [selectingAll, setSelectingAll] = useState(false);

  // On a grid that holds every row, the header checkbox means "all of them".
  // It has to keep meaning that once they are paged, or the bulk actions below
  // it quietly start operating on the first hundred.
  const allSelected = server
    ? server.matching > 0 && selectedIds.length >= server.matching
    : table.getIsAllRowsSelected();
  const someSelected = server
    ? selectedIds.length > 0 && !allSelected
    : table.getIsSomeRowsSelected();

  const toggleAll = async () => {
    if (!server?.selectAll) {
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
      return;
    }
    if (allSelected) {
      table.resetRowSelection();
      return;
    }
    setSelectingAll(true);
    try {
      const ids = await server.selectAll();
      setRowSelection(Object.fromEntries(ids.map((id) => [id, true])));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Something went wrong"));
    } finally {
      setSelectingAll(false);
    }
  };

  const hasFilters = columnFilters.length > 0 || globalFilter.length > 0;
  const tree = Boolean(getSubRows);

  // Column widths, measured once the first rows of a long grid are drawn and
  // then held (fixed layout): with the browser's automatic layout every new
  // batch of rows scrolled into view re-sized the columns to its own text, so
  // the columns jumped sideways under the eye. Measured again when the visible
  // columns change (shown, hidden, moved) or the window is resized.
  const tableRef = useRef<HTMLTableElement>(null);
  const visibleKey = table.getVisibleLeafColumns().map((column) => column.id).join("|");
  const [frozen, setFrozen] = useState<{ key: string; widths: number[] } | null>(null);
  const hasRows = rows.length > 0;
  useLayoutEffect(() => {
    if (!virtualize || !hasRows || frozen?.key === visibleKey) return;
    const header = tableRef.current?.querySelector("thead tr");
    if (!header) return;
    const measured = Array.from(header.children).map((cell) => Math.ceil(cell.getBoundingClientRect().width));
    // The first screenful alone would squeeze a column whose long values come
    // further down: each data column is also sized from every row's value,
    // measured as text, within a cap so one long name cannot take the page.
    // Rows cannot be dragged on a virtualized grid, so no reorder column leads.
    const leading = (tree ? 1 : 0) + (selectable ? 1 : 0);
    const measure = textMeasurer(header as HTMLElement);
    const allRows = table.getRowModel().rows;
    table.getVisibleLeafColumns().forEach((column, index) => {
      const at = leading + index;
      if (at >= measured.length) return;
      let widest = 0;
      for (const row of allRows) {
        const value = row.getValue(column.id);
        if (typeof value === "string" || typeof value === "number") {
          widest = Math.max(widest, measure(String(value)));
        }
      }
      measured[at] = Math.min(Math.max(measured[at] ?? 0, Math.ceil(widest) + CELL_PADDING), MAX_FROZEN_WIDTH);
    });
    setFrozen({ key: visibleKey, widths: measured });
  }, [virtualize, hasRows, visibleKey, frozen, tree, selectable, table]);
  useEffect(() => {
    const unfreeze = () => setFrozen(null);
    window.addEventListener("resize", unfreeze);
    return () => window.removeEventListener("resize", unfreeze);
  }, []);
  const columnWidths = virtualize && frozen?.key === visibleKey ? frozen.widths : null;
  // With grouping parents the counts are of leaves: the families are a way of
  // reading the list, not entries in it.
  const leafCount = (model: { rows: { subRows: unknown[] }[]; flatRows: { subRows: unknown[] }[] }) =>
    groupingParents ? model.flatRows.filter((row) => row.subRows.length === 0).length : model.rows.length;
  // Server mode counts what matches, not what arrived: the browser holds one
  // page and cannot honestly answer "how many" from it.
  const totalCount = server
    ? server.total
    : groupingParents
      ? leafCount(table.getCoreRowModel())
      : data.length;
  const filteredCount = server
    ? server.matching
    : groupingParents
      ? leafCount(table.getFilteredRowModel())
      : rows.length;
  const selectedCount = groupingParents
    ? table.getCoreRowModel().flatRows.filter((row) => row.subRows.length === 0 && rowSelection[row.id]).length
    : selectedIds.length;
  // Reordering a filtered or sorted view would write an order the user can't
  // see, so the handles only appear on the untouched list.
  const reorderable = Boolean(onReorder) && !tree && sorting.length === 0 && !virtualize;
  const actionColumn = editable || Boolean(rowActions);
  /** Width of a full-body row — the empty message and the loading spinner. */
  const bodyColumns =
    table.getVisibleLeafColumns().length +
    (reorderable ? 1 : 0) +
    (tree ? 1 : 0) +
    (selectable ? 1 : 0) +
    (actionColumn ? 1 : 0);

  const dropOn = (targetIndex: number) => {
    if (dragIndex === null || !onReorder || dragIndex === targetIndex) return;
    const next = rows.map((row) => row.original);
    const [moved] = next.splice(dragIndex, 1);
    if (moved !== undefined) next.splice(targetIndex, 0, moved);
    setDragIndex(null);
    onReorder(next);
  };

  const deletable = Boolean(onDeleteSelectedIds ?? onDeleteSelected);

  const deleteSelected = async () => {
    if (selectedCount === 0 || !deletable) return;

    const copy = deleteSelectedConfirm?.(selectedCount) ?? {
      title: t("dialog.delete.selected.title", "Delete the selected rows?"),
    };
    const confirmed = await confirm({
      title: copy.title,
      ...(copy.message ? { message: copy.message } : {}),
      ...(copy.requireText ? { requireText: copy.requireText } : {}),
      ...(copy.consequences ? { consequences: copy.consequences } : {}),
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      // By id where the screen offers it, because the selection may reach past
      // the loaded page and only the ids are complete there.
      if (onDeleteSelectedIds) {
        await onDeleteSelectedIds(selectedIds);
      } else {
        await onDeleteSelected?.(selectedRows);
      }
      // The deleted rows are gone; a stale selection would keep the bulk
      // button live and the count wrong.
      table.resetRowSelection();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Something went wrong"));
    }
  };

  const builtInActions: TableAction[] = [];
  if (editable && editor?.newRow) {
    builtInActions.push({
      label: editor.addLabel ?? t("button.add", "Add"),
      onClick: rowEditor.startAdd,
      icon: <Plus className="size-4" />,
      variant: "primary",
      write: true,
      disabled: rowEditor.editing !== null,
    });
  }
  useEffect(() => {
    if (!handle) return;
    handle.current = { deleteSelected, clearSelection: () => table.resetRowSelection() };
    return () => {
      handle.current = null;
    };
  });

  if (!compact && !hidePrint) {
    builtInActions.push({
      label: t("table.print", "Print"),
      onClick: () =>
        printTable(table, {
          title: printTitle ?? document.querySelector("header h1")?.textContent?.trim() ?? document.title,
          label: (id) => {
            const column = table.getColumn(id);
            const meta = column?.columnDef.meta;
            const header = column?.columnDef.header;
            return meta?.headerKey ? t(meta.headerKey, id) : typeof header === "string" ? header : id;
          },
        }),
      icon: <Printer className="size-4" />,
      iconOnly: true,
    });
  }

  if (deletable && canWrite && !hideDeleteButton) {
    builtInActions.push({
      label: t("button.delete", "Delete"),
      onClick: () => void deleteSelected(),
      icon: <Trash2 className="size-4" />,
      variant: "danger",
      write: true,
      requiresSelection: true,
      iconOnly: deleteIconOnly,
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TableToolbar
        table={table}
        actions={[...builtInActions, ...actions]}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        total={totalCount}
        filtered={filteredCount}
        selected={selectedCount}
        showFilteredBadge={hasFilters}
        extraBadges={extraBadges}
        toolbarExtra={toolbarExtra}
        toolbarLead={toolbarLead}
      />

      {/* The rows themselves are transparent — they carry borders, not fills —
          so without a background here the grid body shows whatever is behind
          it. On a list screen that is invisible, because the grid is
          full-bleed; inside a detail tab the pane's padding puts the page
          ground on all four sides of a grid whose interior is also the page
          ground, and the card dissolves into the page with only its 1px border
          left. The toolbar and header row already paint themselves, which is
          what made the seam look arbitrary rather than simply flat.

          Compact mode is deliberately excluded: there the body is a list of
          cards that are *meant* to float on the ground. */}
      <div
        ref={scrollRef}
        onScroll={server ? loadAhead : undefined}
        className={cn("min-h-0 flex-1 overflow-auto", !compact && "bg-surface")}
      >
        {loading ? (
          <Centered>
            <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </Centered>
        ) : compact ? (
          rows.length === 0 ? (
            <Centered>
              <p className="text-[13px] text-ink-muted">
                {emptyMessage ?? t("table.empty", "Nothing to show")}
              </p>
            </Centered>
          ) : (
            <CardList
              table={table}
              {...(onRowClick ? { onRowClick } : {})}
              {...(rowClassName ? { rowClassName } : {})}
            />
          )
        ) : (
          <table
            ref={tableRef}
            className="w-full border-separate border-spacing-0 text-[13px]"
            style={
              columnWidths
                ? { tableLayout: "fixed", width: columnWidths.reduce((sum, width) => sum + width, 0), minWidth: "100%" }
                : undefined
            }
          >
            {columnWidths && (
              <colgroup>
                {columnWidths.map((width, index) => (
                  <col key={index} style={{ width }} />
                ))}
              </colgroup>
            )}
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {reorderable && (
                    <th className="w-7 border-b border-line bg-surface-sunk px-1 py-1.5" />
                  )}
                  {tree && <th className="w-8 border-b border-line bg-surface-sunk px-1 py-1.5" />}
                  {selectable && (
                    <th className="w-9 border-b border-line bg-surface-sunk p-0">
                      <label className="flex cursor-pointer items-center px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          disabled={selectingAll}
                          ref={(element) => {
                            if (element) element.indeterminate = someSelected;
                          }}
                          onChange={() => void toggleAll()}
                          aria-label={t("table.select-all", "Select all rows")}
                          className="size-3.5 accent-[var(--accent)]"
                        />
                      </label>
                    </th>
                  )}
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta;
                    const sortDirection = header.column.getIsSorted();
                    const sortable = header.column.getCanSort();
                    const dragging = columnDrag?.id === header.column.id;
                    const over = columnDrag?.over === header.column.id ? columnDrag : null;
                    return (
                      <th
                        key={header.id}
                        // Native drag: a click is still a click, the browser
                        // only starts a drag once the pointer moves, so sorting
                        // by clicking the label keeps working.
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", header.column.id);
                          setColumnDrag({ id: header.column.id, over: null, after: false });
                        }}
                        onDragOver={(event) => {
                          if (!columnDrag || columnDrag.id === header.column.id) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          const box = event.currentTarget.getBoundingClientRect();
                          const after = event.clientX > box.left + box.width / 2;
                          if (columnDrag.over !== header.column.id || columnDrag.after !== after) {
                            setColumnDrag({ ...columnDrag, over: header.column.id, after });
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (columnDrag) dropColumn(columnDrag.id, header.column.id, over?.after ?? false);
                          setColumnDrag(null);
                        }}
                        onDragEnd={() => setColumnDrag(null)}
                        className={cn(
                          "group/col relative border-b border-line bg-surface-sunk px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-faint",
                          meta?.align === "right" ? "text-right" : "text-left",
                          dragging && "opacity-40",
                          over && !over.after && "shadow-[inset_2px_0_0_var(--accent)]",
                          over && over.after && "shadow-[inset_-2px_0_0_var(--accent)]",
                        )}
                      >
                        <GripVertical
                          aria-hidden="true"
                          className={cn(
                            "absolute top-1/2 size-3 -translate-y-1/2 cursor-grab text-ink-faint opacity-0 transition-opacity group-hover/col:opacity-60",
                            meta?.align === "right" ? "right-0.5" : "left-0.5",
                          )}
                        />
                        <button
                          type="button"
                          disabled={!sortable}
                          // Shift-click adds to the sort rather than replacing
                          // it, which is how the Vaadin grids multi-sorted.
                          onClick={(event) => header.column.toggleSorting(undefined, event.shiftKey)}
                          className={cn(
                            "inline-flex items-center gap-1",
                            sortable && "hover:text-ink",
                            meta?.align === "right" && "flex-row-reverse",
                          )}
                        >
                          {meta?.headerKey
                            ? t(meta.headerKey, header.column.id)
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {sortable &&
                            (sortDirection === "asc" ? (
                              <ArrowUp className="size-3" />
                            ) : sortDirection === "desc" ? (
                              <ArrowDown className="size-3" />
                            ) : (
                              <ChevronsUpDown className="size-3 opacity-30" />
                            ))}
                        </button>
                      </th>
                    );
                  })}
                  {actionColumn && (
                    <th className="w-24 border-b border-line bg-surface-sunk px-3 py-1.5 text-right text-[11px] font-medium uppercase tracking-wider text-ink-faint">
                      {rowActionsHeader ?? ""}
                    </th>
                  )}
                </tr>
              ))}

              {/* Filter row — one control per column, typed by its meta. */}
              <tr>
                {reorderable && <th className="border-b border-line bg-surface px-1 py-1" />}
                {tree && <th className="border-b border-line bg-surface px-1 py-1" />}
                {selectable && <th className="border-b border-line bg-surface px-2 py-1" />}
                {table.getVisibleLeafColumns().map((column) => (
                  <th key={column.id} className="border-b border-line bg-surface px-2 py-1">
                    {/* The filter follows its column's alignment. Controls that
                        fill the cell (text, select) are unaffected, but the
                        popover triggers are fixed width, and a narrow trigger
                        pinned left under a right-aligned column of figures read
                        as belonging to the column beside it. */}
                    <div
                      className={cn(
                        "flex",
                        column.columnDef.meta?.align === "right" ? "justify-end" : "justify-start",
                      )}
                    >
                      <ColumnFilter column={column} server={Boolean(server)} />
                    </div>
                  </th>
                ))}
                {actionColumn && <th className="border-b border-line bg-surface px-2 py-1" />}
              </tr>
            </thead>

            <tbody>
              {/* The add row sits at the top, outside the sorted/filtered set,
                  so it can't disappear as its fields are filled in. */}
              {rowEditor.editing?.isNew && editor && (
                <EditorRow
                  table={table}
                  editor={editor}
                  editing={rowEditor.editing}
                  selectable={selectable}
                  leading={(reorderable ? 1 : 0) + (tree ? 1 : 0)}
                  onChange={rowEditor.change}
                  onSave={() => void rowEditor.save()}
                  onSaveAndAdd={() => void rowEditor.save({ thenAdd: true })}
                  onCancel={rowEditor.cancel}
                  rowHeight={rowHeight}
                />
              )}

              {/* A filter that empties the result set used to blank the whole
                  table (header, filter row and all), leaving no way back to
                  the very control that caused it. The header now stays; only
                  the body swaps to this message, spanning every column. */}
              {rows.length === 0 && !server?.loadingMore && (
                <tr>
                  <td colSpan={bodyColumns} className="py-12 text-center text-[13px] text-ink-muted">
                    {emptyMessage ?? t("table.empty", "Nothing to show")}
                  </td>
                </tr>
              )}

              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: paddingTop }} />
                </tr>
              )}

              {(virtualize ? virtualRows.map((item) => rows[item.index]!) : rows).map((row, visibleIndex) =>
                rowEditor.editing && !rowEditor.editing.isNew && rowEditor.editing.id === row.id && editor ? (
                  <EditorRow
                    key={row.id}
                    table={table}
                    editor={editor}
                    editing={rowEditor.editing}
                    selectable={selectable}
                    leading={(reorderable ? 1 : 0) + (tree ? 1 : 0)}
                    onChange={rowEditor.change}
                    onSave={() => void rowEditor.save()}
                    onCancel={rowEditor.cancel}
                    rowHeight={rowHeight}
                  />
                ) : (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  {...(reorderable
                    ? {
                        onDragOver: (event: React.DragEvent) => event.preventDefault(),
                        onDrop: () => dropOn(visibleIndex),
                      }
                    : {})}
                  className={cn(
                    "hover:bg-surface-sunk",
                    onRowClick && "cursor-pointer",
                    row.getIsSelected() && "bg-accent-soft",
                    row.depth > 0 && "bg-surface-sunk/40",
                    rowClassName?.(row.original),
                  )}
                >
                  {reorderable && (
                    <td
                      draggable
                      onDragStart={() => setDragIndex(visibleIndex)}
                      onDragEnd={() => setDragIndex(null)}
                      className="cursor-grab border-b border-line-soft px-1 text-ink-faint active:cursor-grabbing"
                    >
                      <GripVertical className="size-3.5" />
                    </td>
                  )}
                  {tree && (
                    <td
                      className="border-b border-line-soft px-1"
                      style={{ paddingLeft: 4 + row.depth * 14 }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {row.getCanExpand() ? (
                        <button
                          type="button"
                          onClick={row.getToggleExpandedHandler()}
                          aria-label={t("table.expand", "Expand")}
                          aria-expanded={row.getIsExpanded()}
                          className="rounded p-0.5 text-ink-muted hover:bg-surface-sunk hover:text-ink"
                        >
                          {row.getIsExpanded() ? (
                            <ChevronDown className="size-3.5" />
                          ) : (
                            <ChevronRight className="size-3.5" />
                          )}
                        </button>
                      ) : null}
                    </td>
                  )}
                  {/* The whole row navigates, so this cell has to swallow its
                      own clicks the way the tree and action cells do. Without
                      that, the padding around a 14px checkbox was a live link
                      into the record, and ticking a row was a game of accuracy.
                      The label then turns that same padding into a bigger
                      target for the checkbox instead of dead space. */}
                  {selectable && (
                    <td
                      className="border-b border-line-soft p-0"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {/* Regular cursor: the hand over a whole cell suggested a
                          link where there is none. The label still makes the
                          padding part of the target. */}
                      <label className="flex h-full cursor-default items-center px-2 py-2">
                        <input
                          type="checkbox"
                          checked={row.getIsSelected()}
                          onChange={row.getToggleSelectedHandler()}
                          aria-label={t("table.select-row", "Select row")}
                          className="size-3.5 accent-[var(--accent)]"
                        />
                      </label>
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{ height: rowHeight }}
                      className={cn(
                        "truncate border-b border-line-soft px-3 text-ink",
                        cell.column.columnDef.meta?.align === "right" && "tabular text-right",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}

                  {actionColumn && (
                    <td
                      className="border-b border-line-soft px-3 text-right"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-0.5">
                        {rowActions?.(row.original)}
                        {editable && editor && (
                          <>
                            {editor.rowActions?.(row.original)}
                            <IconAction
                              label={t("button.edit", "Edit")}
                              icon={<Pencil className="size-3.5" />}
                              onClick={() => rowEditor.startEdit(row.original)}
                              disabled={rowEditor.editing !== null}
                            />
                            {editor.onDelete && (
                              <IconAction
                                label={t("button.delete", "Delete")}
                                icon={<Trash2 className="size-3.5" />}
                                onClick={() => void rowEditor.remove(row.original)}
                                disabled={rowEditor.editing !== null}
                                danger
                              />
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                ),
              )}

              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: paddingBottom }} />
                </tr>
              )}

              {/* The list continues rather than ending, so the wait for the
                  next page has to look like a wait and not like the bottom. */}
              {server?.loadingMore && (
                <tr>
                  <td colSpan={bodyColumns} className="py-3 text-center">
                    <span className="inline-block size-4 animate-spin rounded-full border-2 border-accent border-t-transparent align-middle" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex h-40 items-center justify-center">{children}</div>;
}

/**
 * A row in edit mode: editable columns become inputs, the rest keep rendering
 * their normal cell, and the action column swaps to Save/Cancel.
 */
function EditorRow<T>({
  table,
  editor,
  editing,
  selectable,
  leading = 0,
  onChange,
  onSave,
  onSaveAndAdd,
  onCancel,
  rowHeight,
}: {
  table: import("@tanstack/react-table").Table<T>;
  editor: RowEditorConfig<T>;
  editing: import("./rowEditor").EditingState<T>;
  selectable: boolean;
  /** Blank cells matching the grid's drag/expander columns, so columns line up. */
  leading?: number;
  onChange: (draft: T) => void;
  onSave: () => void;
  onSaveAndAdd?: () => void;
  onCancel: () => void;
  rowHeight: number;
}) {
  const { t } = useTranslation();
  const visibleColumns = table.getVisibleLeafColumns();
  // A field read only on this row is shown as its cell, like a column with no field.
  const editableHere = (columnId: string) => {
    const field = editor.fields[columnId];
    return Boolean(field) && !field!.readOnly?.(editing.draft);
  };
  const editableIds = visibleColumns.filter((column) => editableHere(column.id)).map((c) => c.id);
  const lastEditableId = editableIds[editableIds.length - 1];
  // The row being edited, as the grid shows it: its read-only columns keep
  // their normal cell, so the person typing a count still sees which part it
  // is. A new row has nothing to show yet.
  const shown = editing.isNew ? undefined : (() => {
    try {
      return table.getRow(editing.id, true);
    } catch {
      return undefined;
    }
  })();
  const readOnlyCell = (columnId: string) => {
    const cell = shown?.getVisibleCells().find((candidate) => candidate.column.id === columnId);
    return cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null;
  };

  return (
    <tr className={cn("bg-accent-soft", editing.saving && "opacity-60")}>
      {Array.from({ length: leading }, (_, index) => (
        <td key={index} className="border-b border-line-soft px-1" />
      ))}
      {selectable && <td className="border-b border-line-soft px-2" />}

      {visibleColumns.map((column, index) => {
        const field = editableHere(column.id) ? editor.fields[column.id] : undefined;
        return (
          <td key={column.id} style={{ height: rowHeight }} className="border-b border-line-soft px-2">
            {field ? (
              <EditorCell
                field={field}
                draft={editing.draft}
                error={editing.errors[column.id]}
                onChange={onChange}
                onCommit={onSave}
                onCancel={onCancel}
                autoFocus={index === visibleColumns.findIndex((c) => editableHere(c.id))}
                isLast={column.id === lastEditableId}
                {...(editing.isNew && onSaveAndAdd ? { onLeaveLast: onSaveAndAdd } : {})}
              />
            ) : (
              <div className="truncate text-ink-muted">{readOnlyCell(column.id)}</div>
            )}
          </td>
        );
      })}

      <td className="border-b border-line-soft px-3 text-right">
        <div className="flex items-center justify-end gap-0.5">
          <IconAction
            label={t("button.save", "Save")}
            icon={<Check className="size-3.5" />}
            onClick={onSave}
            disabled={editing.saving}
            accent
          />
          <IconAction
            label={t("button.cancel", "Cancel")}
            icon={<X className="size-3.5" />}
            onClick={onCancel}
            disabled={editing.saving}
          />
        </div>
      </td>
    </tr>
  );
}

function IconAction({
  label,
  icon,
  onClick,
  disabled,
  danger,
  accent,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  accent?: boolean;
}) {
  return (
    <Button
      size="sm"
      variant="ghost"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn("size-7 p-0", danger && "hover:text-risk", accent && "text-accent")}
      icon={icon}
    />
  );
}

function ColumnFilter<T>({
  column,
  server,
}: {
  column: import("@tanstack/react-table").Column<T>;
  /** Server-paged: the loaded rows are one page, not a sample of the values. */
  server: boolean;
}) {
  const { t } = useTranslation();
  const meta = column.columnDef.meta;
  const kind = meta?.filter ?? "text";

  if (kind === "none" || !column.getCanFilter()) return null;

  const value = (column.getFilterValue() ?? "") as string;
  const shared =
    "h-6 w-full rounded border border-line bg-surface px-1.5 text-[12px] text-ink placeholder:text-ink-faint focus-visible:outline-1 focus-visible:outline-accent";

  /**
   * Choices for a select or multi-select: declared by the column, or read off
   * the data when it is all here.
   *
   * Server-paged grids get the declared ones only. Collecting them from the
   * loaded page would offer whichever handful of values the first hundred rows
   * happened to contain and silently hide the rest — a dropdown that is wrong
   * in a way nobody can see, which is worse than one that is visibly empty.
   */
  const options = () =>
    meta?.filterOptions ??
    (server
      ? []
      : Array.from(column.getFacetedUniqueValues().keys())
          .filter((entry): entry is string => typeof entry === "string" && entry !== "")
          .sort()
          .map((entry) => ({ value: entry, label: entry })));

  if (kind === "select") {
    const choices = options();

    return (
      <select
        value={value}
        onChange={(event) => column.setFilterValue(event.target.value || undefined)}
        aria-label={t("grid.filter", "Filter")}
        className={shared}
      >
        <option value="">{t("grid.filter.all", "All")}</option>
        {choices.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  if (kind === "multiselect") {
    const choices = options();
    const selected = (column.getFilterValue() as string[] | undefined) ?? [];

    return (
      <div className="min-w-32">
        <MultiSelect
          size="sm"
          options={choices}
          value={selected}
          placeholder={t("grid.filter.all", "All")}
          onChange={(next) => column.setFilterValue(next.length > 0 ? next : undefined)}
        />
      </div>
    );
  }

  if (kind === "range") {
    // Two bounds, either optional — the `IntegerFromToFilter` shape. Clearing
    // both drops the filter entirely so the row count badge stays honest.
    // Floated in a popover rather than squeezed into the filter row's cell,
    // which the column's own width can pinch to a few dozen pixels.
    const [from, to] = (column.getFilterValue() as RangeFilterValue | undefined) ?? [
      undefined,
      undefined,
    ];
    const update = (next: RangeFilterValue) =>
      column.setFilterValue(next[0] === undefined && next[1] === undefined ? undefined : next);
    return <RangeFilterPopover from={from} to={to} onChange={update} />;
  }

  if (kind === "daterange") {
    const [from, to] = (column.getFilterValue() as DateRangeFilterValue | undefined) ?? [
      undefined,
      undefined,
    ];
    const update = (next: DateRangeFilterValue) =>
      column.setFilterValue(next[0] === undefined && next[1] === undefined ? undefined : next);
    return <DateRangeFilterPopover from={from} to={to} onChange={update} />;
  }

  return (
    <input
      type={kind === "number" ? "number" : "text"}
      value={value}
      onChange={(event) => column.setFilterValue(event.target.value || undefined)}
      placeholder={t("grid.filter", "Filter")}
      aria-label={t("grid.filter", "Filter")}
      className={shared}
    />
  );
}

/**
 * Portal-and-position plumbing shared by the two floating filter panels
 * below: positioned against the viewport rather than the trigger, for the
 * same reason `MultiSelect` is — an absolutely-positioned child gets clipped
 * by whatever happens to scroll it, and the filter row scrolls with the
 * table body. Closes on outside click or Escape, repositions on scroll or
 * resize.
 */
/** Matches the panel's `w-48` below — needed up front to know if it overflows. */
const FILTER_PANEL_WIDTH = 192;

interface FloatingFilterAnchor {
  top: number;
  /** Grows rightward from the trigger's left edge — the common case. */
  left?: number;
  /**
   * Grows leftward from the trigger's right edge instead, once the trigger
   * sits close enough to the viewport's right edge (the last column or two
   * of a wide grid) that a left-anchored panel would run off-screen.
   */
  right?: number;
}

function useFloatingFilterPanel() {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<FloatingFilterAnchor | null>(null);

  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const top = rect.bottom + 4;
    if (rect.left + FILTER_PANEL_WIDTH + margin > window.innerWidth) {
      setAnchor({ top, right: window.innerWidth - rect.right });
    } else {
      setAnchor({ top, left: rect.left });
    }
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!container.current?.contains(target) && !panel.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  return { open, setOpen, container, trigger, panel, anchor };
}

/**
 * The filter-row cell for a floating panel: a fixed-width square-ish button
 * showing either the active summary or a placeholder.
 *
 * Fixed width, not `w-full` — the table has no `table-layout: fixed`, so an
 * unset-size column's width is the browser's auto-layout guess from its
 * widest cell, filter row included. A button whose text changes on every
 * keystroke (the summary) fed that guess a moving target: the column, and
 * so the whole table, visibly resized as you typed, which the clamped
 * `scrollLeft` read back as the grid jumping to the left. A fixed-width
 * trigger removes the column from that guess entirely.
 */
function FilterTrigger({
  triggerRef,
  open,
  active,
  summary,
  placeholder,
  onClick,
}: {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  open: boolean;
  active: boolean;
  summary: string | null;
  placeholder: string;
  onClick: () => void;
}) {
  return (
    <button
      ref={triggerRef}
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={open}
      className={cn(
        "flex h-6 w-24 items-center justify-between gap-1 rounded border border-line bg-surface px-1.5 text-[12px] text-ink",
        "focus-visible:outline-1 focus-visible:outline-accent",
        active && "border-accent text-ink",
      )}
    >
      <span className={cn("truncate", !summary && "text-ink-faint")}>{summary ?? placeholder}</span>
      <SlidersHorizontal className="size-3 shrink-0 text-ink-faint" />
    </button>
  );
}

function FilterPanel({
  panelRef,
  anchor,
  children,
}: {
  panelRef: React.RefObject<HTMLDivElement | null>;
  anchor: FloatingFilterAnchor;
  children: ReactNode;
}) {
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      style={{
        position: "fixed",
        top: anchor.top,
        ...(anchor.left !== undefined ? { left: anchor.left } : { right: anchor.right }),
      }}
      className="z-50 flex w-48 flex-col gap-2 rounded-md border border-line bg-surface-over p-3 shadow-overlay"
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * A `range` column's filter, floated in a panel instead of squeezed into the
 * filter row's cell.
 *
 * Experimental — used on one column of the products grid for now.
 */
function RangeFilterPopover({
  from,
  to,
  onChange,
}: {
  from: number | undefined;
  to: number | undefined;
  onChange: (next: RangeFilterValue) => void;
}) {
  const { t } = useTranslation();
  const { open, setOpen, container, trigger, panel, anchor } = useFloatingFilterPanel();

  const active = from !== undefined || to !== undefined;
  // "120 – 450", "≥ 120" or "≤ 450" — whichever bound is actually set.
  const summary =
    from !== undefined && to !== undefined
      ? `${from} – ${to}`
      : from !== undefined
        ? `≥ ${from}`
        : to !== undefined
          ? `≤ ${to}`
          : null;

  return (
    <div ref={container} className="relative">
      <FilterTrigger
        triggerRef={trigger}
        open={open}
        active={active}
        summary={summary}
        placeholder={t("grid.filter.range", "Range")}
        onClick={() => setOpen((current) => !current)}
      />

      {open && anchor && (
        <FilterPanel panelRef={panel} anchor={anchor}>
          <label className="flex flex-col gap-1 text-[12px] text-ink-muted">
            {t("grid.filter.from", "From")}
            <input
              type="number"
              autoFocus
              value={from ?? ""}
              onChange={(event) =>
                onChange([event.target.value === "" ? undefined : Number(event.target.value), to])
              }
              className="h-8 w-full rounded border border-line bg-surface px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-ink-muted">
            {t("grid.filter.to", "To")}
            <input
              type="number"
              value={to ?? ""}
              onChange={(event) =>
                onChange([from, event.target.value === "" ? undefined : Number(event.target.value)])
              }
              className="h-8 w-full rounded border border-line bg-surface px-2 text-[13px] text-ink"
            />
          </label>
          {active && (
            <button
              type="button"
              onClick={() => {
                onChange([undefined, undefined]);
                setOpen(false);
              }}
              className="self-start text-[12px] text-ink-muted hover:text-ink hover:underline"
            >
              {t("grid.filter.clear", "Clear")}
            </button>
          )}
        </FilterPanel>
      )}
    </div>
  );
}

/** A `daterange` column's filter — same panel as `RangeFilterPopover`, date inputs. */
function DateRangeFilterPopover({
  from,
  to,
  onChange,
}: {
  from: string | undefined;
  to: string | undefined;
  onChange: (next: DateRangeFilterValue) => void;
}) {
  const { t } = useTranslation();
  const { open, setOpen, container, trigger, panel, anchor } = useFloatingFilterPanel();

  const active = from !== undefined || to !== undefined;
  const summary =
    from !== undefined && to !== undefined
      ? `${from} → ${to}`
      : from !== undefined
        ? `${t("grid.filter.from", "From")} ${from}`
        : to !== undefined
          ? `${t("grid.filter.to", "To")} ${to}`
          : null;

  return (
    <div ref={container} className="relative">
      <FilterTrigger
        triggerRef={trigger}
        open={open}
        active={active}
        summary={summary}
        placeholder={t("grid.filter.daterange", "Dates")}
        onClick={() => setOpen((current) => !current)}
      />

      {open && anchor && (
        <FilterPanel panelRef={panel} anchor={anchor}>
          <label className="flex flex-col gap-1 text-[12px] text-ink-muted">
            {t("grid.filter.from", "From")}
            <input
              type="date"
              autoFocus
              value={from ?? ""}
              onChange={(event) => onChange([event.target.value || undefined, to])}
              className="h-8 w-full rounded border border-line bg-surface px-2 text-[13px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-ink-muted">
            {t("grid.filter.to", "To")}
            <input
              type="date"
              value={to ?? ""}
              onChange={(event) => onChange([from, event.target.value || undefined])}
              className="h-8 w-full rounded border border-line bg-surface px-2 text-[13px] text-ink"
            />
          </label>
          {active && (
            <button
              type="button"
              onClick={() => {
                onChange([undefined, undefined]);
                setOpen(false);
              }}
              className="self-start text-[12px] text-ink-muted hover:text-ink hover:underline"
            >
              {t("grid.filter.clear", "Clear")}
            </button>
          )}
        </FilterPanel>
      )}
    </div>
  );
}

/**
 * Turns the grid's filter state into the server's shape: one entry per active
 * filter, field name to values.
 *
 * Each value is encoded by the kind its column declares, because the value
 * alone is ambiguous — `["10", "50"]` is two chosen options from a multi-select
 * and a from/to pair from a range, and the server reads them differently. A
 * range keeps both slots even when one is blank, so "up to 500" cannot be
 * mistaken for "from 500".
 */
function serverFilters<T>(
  filters: ColumnFiltersState,
  columns: ColumnDef<T>[],
): Record<string, string[]> {
  const kinds = new Map<string, FilterKind>(
    columns.map((column) => [String(column.id), column.meta?.filter ?? "text"]),
  );
  const encoded: Record<string, string[]> = {};
  for (const { id, value } of filters) {
    const values = encodeFilter(kinds.get(id) ?? "text", value);
    if (values.length > 0) encoded[id] = values;
  }
  return encoded;
}

function encodeFilter(kind: FilterKind, value: unknown): string[] {
  if (value === undefined || value === null || value === "") return [];

  if (kind === "multiselect") {
    if (!Array.isArray(value)) return [];
    return value.map(String).filter((entry) => entry !== "");
  }

  if (kind === "range" || kind === "daterange") {
    const [from, to] = Array.isArray(value) ? value : [undefined, undefined];
    if (isBlank(from) && isBlank(to)) return [];
    return [isBlank(from) ? "" : String(from), isBlank(to) ? "" : String(to)];
  }

  return [String(value)];
}

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}
